/**
 * /mail/templates — Templates CRUD with preview (OCP-131).
 *
 * Now full create/edit/delete after OMS-35 shipped the customer-
 * scoped admin endpoints. Variables-schema is a simple key→type
 * table editor (string/number/boolean) — covers 95% of use cases
 * without needing a full JSON-schema editor.
 *
 * Preview pane shows the rendered output by substituting sample
 * values into the body — client-side {{name}} replacement.
 *
 * OCP-544: the rendered preview goes through <SafeHtmlPreview> (a
 * sandboxed opaque-origin iframe). Template HTML is untrusted input -
 * any member of the customer can write it, any other member reads it -
 * so it must never reach this document's DOM. Do not swap the component
 * for a raw innerHTML sink; eslint blocks that repo-wide.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { MailReadOnlyNotice } from '../components/MailReadOnlyNotice';
import { SafeHtmlPreview } from '../components/SafeHtmlPreview';
import { ApiError, api } from '../lib/api';
import { useMailWriteAccess } from '../lib/use-mail-write-access';
import { fmtDate } from '../lib/format-time';

interface TemplateSummary {
  id: string;
  customer_id: string | null;
  name: string;
  subject: string | null;
  variable_count: number;
  updated_at: string | null;
}

interface TemplateDetail extends TemplateSummary {
  body_html: string | null;
  body_text: string | null;
  variables_schema: Record<string, unknown> | null;
}

interface ListResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  templates: TemplateSummary[];
  cursor: string | null;
}

type Modal =
  | { kind: 'create' }
  | { kind: 'edit'; detail: TemplateDetail }
  | { kind: 'confirm-delete'; template: TemplateSummary }
  | null;

export function MailTemplatesPage() {
  // `t` is the template loop variable in this file; the catalogue function is `tr`.
  const { t: tr } = useTranslation('mail-templates');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [response, setResponse] = useState<ListResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [selected, setSelected] = useState<TemplateDetail | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  // OCP-545 - viewer / billing-only members may read the mail setup but
  // not change it; the API answers 403 either way.
  const { canWrite } = useMailWriteAccess();

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (searchDebounced) p.set('search', searchDebounced);
    p.set('limit', '100');
    return p.toString();
  }, [searchDebounced]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api<ListResponse>(`/v1/portal/mail/templates?${qs}`);
      setResponse(r);
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorDetail(
          err.status === 502 ? tr('error.unreachable') : tr('error.loadHttp', { status: err.status }),
        );
      } else {
        setErrorDetail(tr('error.load'));
      }
      setState('error');
    }
  }, [qs, tr]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openTemplate(t: TemplateSummary): Promise<void> {
    try {
      const r = await api<TemplateDetail>(`/v1/portal/mail/templates/${t.id}`);
      setSelected(r);
    } catch {
      setErrorDetail(tr('error.loadOne'));
    }
  }

  async function openForEdit(t: TemplateSummary): Promise<void> {
    try {
      const r = await api<TemplateDetail>(`/v1/portal/mail/templates/${t.id}`);
      setModal({ kind: 'edit', detail: r });
    } catch {
      setErrorDetail(tr('error.loadForEdit'));
    }
  }

  async function createTemplate(body: TemplateFormValue): Promise<void> {
    try {
      const created = await api<TemplateDetail>('/v1/portal/mail/templates', {
        method: 'POST',
        body: body as unknown as Record<string, unknown>,
      });
      setModal(null);
      setSelected(created);
      void load();
    } catch (err) {
      setErrorDetail(
        describeWriteError(tr, err, tr('error.createValidator'), tr('error.create')),
      );
    }
  }

  async function updateTemplate(id: string, body: TemplateFormValue): Promise<void> {
    try {
      const updated = await api<TemplateDetail>(`/v1/portal/mail/templates/${id}`, {
        method: 'PATCH',
        body: body as unknown as Record<string, unknown>,
      });
      setModal(null);
      setSelected(updated);
      void load();
    } catch (err) {
      setErrorDetail(
        describeWriteError(tr, err, tr('error.updateValidator'), tr('error.update')),
      );
    }
  }

  async function deleteTemplate(id: string): Promise<void> {
    try {
      await api(`/v1/portal/mail/templates/${id}`, { method: 'DELETE' });
      setModal(null);
      setSelected((prev) => (prev?.id === id ? null : prev));
      setResponse((prev) =>
        prev ? { ...prev, templates: prev.templates.filter((t) => t.id !== id) } : prev,
      );
    } catch {
      setErrorDetail(tr('error.delete'));
    }
  }

  if (response && !response.oms_configured) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 p-5 text-sm text-amber-900 dark:text-amber-200">
        <h2 className="text-base font-semibold">{tr('unavailable.title')}</h2>
        <p className="mt-1">{tr('unavailable.text')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">{tr('search')}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr('searchPlaceholder')}
            className="w-72 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        {canWrite && (
          <button
            type="button"
            onClick={() => setModal({ kind: 'create' })}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            {tr('new')}
          </button>
        )}
      </div>

      <MailReadOnlyNotice />

      {errorDetail && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {errorDetail}
        </div>
      )}

      <div className="flex flex-col gap-4 md:flex-row">
        <div className={selected ? 'w-full md:w-1/2' : 'w-full'}>
          {response && response.templates.length === 0 && state === 'idle' ? (
            <EmptyState hasFilter={Boolean(searchDebounced)} />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">{tr('table.name')}</th>
                    <th className="px-3 py-2">{tr('table.subject')}</th>
                    <th className="px-3 py-2">{tr('table.vars')}</th>
                    <th className="px-3 py-2">{tr('table.updated')}</th>
                    <th className="px-3 py-2 text-right">{tr('table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {response?.templates.map((t) => (
                    <tr
                      key={t.id}
                      className={`${selected?.id === t.id ? 'bg-brand-50/50' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
                    >
                      <td
                        className="cursor-pointer px-3 py-2 font-medium text-gray-900 dark:text-gray-100"
                        onClick={() => void openTemplate(t)}
                      >
                        {t.name}
                      </td>
                      <td
                        className="cursor-pointer truncate px-3 py-2 text-gray-700 dark:text-gray-300"
                        onClick={() => void openTemplate(t)}
                      >
                        {t.subject ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {t.variable_count}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {t.updated_at ? formatDate(t.updated_at) : '—'}
                      </td>
                      <td className="space-x-2 px-3 py-2 text-right">
                        {canWrite ? (
                          <>
                            <button
                              type="button"
                              onClick={() => void openForEdit(t)}
                              className="text-xs text-brand-500 hover:underline"
                            >
                              {tr('table.edit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setModal({ kind: 'confirm-delete', template: t })}
                              className="text-xs text-red-700 dark:text-red-300 hover:underline"
                            >
                              {tr('table.delete')}
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">{tr('table.readOnly')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selected && (
          <TemplatePreviewPanel
            template={selected}
            onEdit={
              canWrite ? () => setModal({ kind: 'edit', detail: selected }) : undefined
            }
            onClose={() => setSelected(null)}
          />
        )}
      </div>

      {modal?.kind === 'create' && (
        <TemplateFormModal
          title={tr('form.newTitle')}
          initial={{ name: '', subject: '', body_html: '', body_text: '', variables: [] }}
          onCancel={() => setModal(null)}
          onSubmit={(v) => void createTemplate(v)}
        />
      )}
      {modal?.kind === 'edit' && (
        <TemplateFormModal
          title={tr('form.editTitle', { name: modal.detail.name })}
          initial={{
            name: modal.detail.name,
            subject: modal.detail.subject ?? '',
            body_html: modal.detail.body_html ?? '',
            body_text: modal.detail.body_text ?? '',
            variables: schemaToList(modal.detail.variables_schema),
          }}
          onCancel={() => setModal(null)}
          onSubmit={(v) => void updateTemplate(modal.detail.id, v)}
        />
      )}
      {modal?.kind === 'confirm-delete' && (
        <ConfirmDeleteModal
          template={modal.template}
          onCancel={() => setModal(null)}
          onConfirm={() => void deleteTemplate(modal.template.id)}
        />
      )}
    </div>
  );
}

/**
 * OCP-544 - human copy for the store-time active-content refusal. The
 * API answers `400 {error:'unsafe_template_html', vectors:[...]}`; a
 * generic "rejected by validator" would leave the customer guessing
 * which line of their template is the problem.
 */
const ACTIVE_CONTENT_VECTORS = ['script_tag', 'event_handler', 'javascript_url', 'embedded_object', 'srcdoc_attribute', 'meta_refresh', 'css_expression'];

function describeWriteError(
  tr: TFunction<'mail-templates'>,
  err: unknown,
  validatorMessage: string,
  fallbackMessage: string,
): string {
  if (!(err instanceof ApiError)) return fallbackMessage;
  if (err.code === 'unsafe_template_html') {
    const raw = (err.body as { vectors?: unknown } | null)?.vectors;
    const named = Array.isArray(raw)
      ? raw
          .map((v) => (ACTIVE_CONTENT_VECTORS.includes(String(v)) ? tr(`vectors.${String(v)}`) : String(v)))
          .filter((v, i, all) => all.indexOf(v) === i)
      : [];
    const detail = named.length > 0 ? tr('error.activeContentFound', { vectors: named.join(', ') }) : '';
    return tr('error.activeContent', { detail });
  }
  if (err.status === 400) return validatorMessage;
  return fallbackMessage;
}

interface VariableRow {
  name: string;
  type: 'string' | 'number' | 'boolean';
}

interface TemplateFormValue {
  name: string;
  subject?: string;
  body_html?: string;
  body_text?: string;
  variables_schema?: Record<string, unknown>;
}

interface TemplateFormState {
  name: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables: VariableRow[];
}

function TemplateFormModal({
  title,
  initial,
  onCancel,
  onSubmit,
}: {
  title: string;
  initial: TemplateFormState;
  onCancel: () => void;
  onSubmit: (v: TemplateFormValue) => void;
}) {
  const { t: tr } = useTranslation('mail-templates');
  const [form, setForm] = useState<TemplateFormState>(initial);
  const [tab, setTab] = useState<'html' | 'text' | 'preview'>('html');
  const valid = form.name.trim().length > 0 && (form.body_html.trim().length > 0 || form.body_text.trim().length > 0);
  function addVariable() {
    setForm((p) => ({ ...p, variables: [...p.variables, { name: '', type: 'string' }] }));
  }
  function removeVariable(i: number) {
    setForm((p) => ({ ...p, variables: p.variables.filter((_, idx) => idx !== i) }));
  }
  function updateVariable(i: number, patch: Partial<VariableRow>) {
    setForm((p) => ({
      ...p,
      variables: p.variables.map((v, idx) => (idx === i ? { ...v, ...patch } : v)),
    }));
  }
  function submit() {
    const variables_schema =
      form.variables.length > 0
        ? {
            type: 'object',
            properties: Object.fromEntries(
              form.variables.filter((v) => v.name.trim()).map((v) => [v.name.trim(), { type: v.type }]),
            ),
          }
        : undefined;
    onSubmit({
      name: form.name.trim(),
      subject: form.subject.trim() || undefined,
      body_html: form.body_html.trim() || undefined,
      body_text: form.body_text.trim() || undefined,
      variables_schema,
    });
  }
  return (
    <ModalShell title={title} titleId="tpl-form-title" onCancel={onCancel} wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 dark:text-gray-300">{tr('form.name')}</span>
          <input
            type="text"
            autoFocus
            maxLength={128}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={tr('form.namePlaceholder')}
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 dark:text-gray-300">{tr('form.subject')}</span>
          <input
            type="text"
            maxLength={256}
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder={tr('form.subjectPlaceholder', { skipInterpolation: true })}
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <fieldset className="mt-4">
        <legend className="text-xs font-medium text-gray-700 dark:text-gray-300">{tr('form.variables')}</legend>
        <p className="mt-0.5 text-xs text-gray-500">
          {tr('form.variablesHelpBefore')}{' '}
          <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">{`{{variableName}}`}</code>{' '}
          {tr('form.variablesHelpAfter')}
        </p>
        <div className="mt-2 space-y-1">
          {form.variables.map((v, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={v.name}
                onChange={(e) => updateVariable(i, { name: e.target.value })}
                placeholder={tr('form.variablePlaceholder')}
                className="w-48 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs"
              />
              <select
                value={v.type}
                onChange={(e) => updateVariable(i, { type: e.target.value as VariableRow['type'] })}
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs"
              >
                <option value="string">string</option>
                <option value="number">number</option>
                <option value="boolean">boolean</option>
              </select>
              <button
                type="button"
                onClick={() => removeVariable(i)}
                className="text-xs text-red-700 dark:text-red-300 hover:underline"
              >
                {tr('form.remove')}
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addVariable}
            className="text-xs text-brand-500 hover:underline"
          >
            {tr('form.addVariable')}
          </button>
        </div>
      </fieldset>

      <div className="mt-5">
        <nav className="flex gap-1 border-b border-gray-200 dark:border-gray-800" aria-label={tr('form.tabsAria')}>
          <TabButton active={tab === 'html'} onClick={() => setTab('html')}>{tr('form.htmlBody')}</TabButton>
          <TabButton active={tab === 'text'} onClick={() => setTab('text')}>{tr('form.textBody')}</TabButton>
          <TabButton active={tab === 'preview'} onClick={() => setTab('preview')}>{tr('form.preview')}</TabButton>
        </nav>
        {tab === 'html' && (
          <textarea
            value={form.body_html}
            onChange={(e) => setForm({ ...form, body_html: e.target.value })}
            placeholder={tr('form.htmlPlaceholder', { skipInterpolation: true })}
            className="mt-2 h-48 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-xs"
          />
        )}
        {tab === 'text' && (
          <textarea
            value={form.body_text}
            onChange={(e) => setForm({ ...form, body_text: e.target.value })}
            placeholder={tr('form.textPlaceholder', { skipInterpolation: true })}
            className="mt-2 h-48 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-xs"
          />
        )}
        {tab === 'preview' && <PreviewPane form={form} />}
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {tr('form.cancel')}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!valid}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-300"
        >
          {tr('form.save')}
        </button>
      </div>
    </ModalShell>
  );
}

function TabButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-1.5 text-xs ${
        active
          ? 'border-brand-500 text-brand-700'
          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

function PreviewPane({ form }: { form: TemplateFormState }) {
  const { t: tr } = useTranslation('mail-templates');
  // Substitute sample values: string → 'sample', number → 42, boolean → true.
  // Lets the customer see what {{name}} placeholders look like rendered.
  const sample: Record<string, string> = Object.fromEntries(
    form.variables
      .filter((v) => v.name.trim())
      .map((v) => [
        v.name.trim(),
        v.type === 'string'
          ? `sample-${v.name}`
          : v.type === 'number'
            ? '42'
            : 'true',
      ]),
  );
  function render(s: string): string {
    return s.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      sample[k] !== undefined ? sample[k] : `{{${k}}}`,
    );
  }
  return (
    <div className="mt-2 space-y-3">
      {form.subject && (
        <div>
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{tr('previewPane.subject')}</div>
          <div className="mt-1 rounded bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm">
            {render(form.subject)}
          </div>
        </div>
      )}
      {form.body_html && (
        <div>
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{tr('previewPane.html')}</div>
          {/*
            OCP-544 - rendered inside a fully sandboxed, opaque-origin
            iframe. Template HTML is authored by any member of the
            customer and read here by another one, so it must never
            touch the portal DOM.
          */}
          <SafeHtmlPreview
            testId="template-preview-html"
            title={tr('previewPane.htmlTitle')}
            className="mt-1"
            html={render(form.body_html)}
          />
          <p className="mt-1 text-xs text-gray-500">
            {tr('previewPane.sandboxNote')}
          </p>
        </div>
      )}
      {form.body_text && (
        <div>
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{tr('previewPane.text')}</div>
          <pre className="mt-1 rounded bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs">{render(form.body_text)}</pre>
        </div>
      )}
    </div>
  );
}

function TemplatePreviewPanel({
  template,
  onEdit,
  onClose,
}: {
  template: TemplateDetail;
  /** OCP-545 - omitted for read-only roles, which hides the Edit control. */
  onEdit?: () => void;
  onClose: () => void;
}) {
  const { t: tr } = useTranslation('mail-templates');
  return (
    <aside className="w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm md:w-1/2">
      <header className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold">{template.name}</h2>
          {template.subject && (
            <p className="text-xs text-gray-500">{tr('panel.subject', { subject: template.subject })}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="rounded bg-brand-500 px-2 py-1 text-xs font-medium text-white hover:bg-brand-600"
            >
              {tr('panel.edit')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={tr('panel.close')}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60"
          >
            ✕
          </button>
        </div>
      </header>
      <div className="flex flex-col gap-4 px-4 py-3 text-xs">
        {template.variables_schema && (
          <section>
            <h3 className="mb-1 font-semibold uppercase text-gray-500">{tr('panel.variables')}</h3>
            <pre className="max-h-40 overflow-auto rounded bg-gray-50 dark:bg-gray-800 p-2">
              {JSON.stringify(template.variables_schema, null, 2)}
            </pre>
          </section>
        )}
        {template.body_html && (
          <section>
            <h3 className="mb-1 font-semibold uppercase text-gray-500">{tr('panel.rendered')}</h3>
            {/* OCP-544 - same sandboxed frame as the edit preview. */}
            <SafeHtmlPreview
              testId="template-detail-rendered"
              title={tr('panel.renderedTitle', { name: template.name })}
              heightClassName="h-56"
              html={template.body_html}
            />
          </section>
        )}
        {template.body_html && (
          <section>
            <h3 className="mb-1 font-semibold uppercase text-gray-500">{tr('panel.htmlBody')}</h3>
            <pre className="max-h-72 overflow-auto rounded bg-gray-50 dark:bg-gray-800 p-2">
              {template.body_html}
            </pre>
          </section>
        )}
        {template.body_text && (
          <section>
            <h3 className="mb-1 font-semibold uppercase text-gray-500">{tr('panel.textBody')}</h3>
            <pre className="max-h-72 overflow-auto rounded bg-gray-50 dark:bg-gray-800 p-2">
              {template.body_text}
            </pre>
          </section>
        )}
      </div>
    </aside>
  );
}

function ConfirmDeleteModal({
  template,
  onCancel,
  onConfirm,
}: {
  template: TemplateSummary;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t: tr } = useTranslation('mail-templates');
  return (
    <ModalShell
      title={tr('deleteModal.title', { name: template.name })}
      titleId="tpl-del-title"
      onCancel={onCancel}
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {tr('deleteModal.text')}
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {tr('deleteModal.cancel')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          {tr('deleteModal.delete')}
        </button>
      </div>
    </ModalShell>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  const { t: tr } = useTranslation('mail-templates');
  return (
    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
      {hasFilter ? tr('empty.noMatch') : tr('empty.none')}
    </div>
  );
}

function ModalShell({
  title,
  titleId,
  children,
  onCancel,
  wide,
}: {
  title: string;
  titleId: string;
  children: React.ReactNode;
  onCancel: () => void;
  wide?: boolean;
}) {
  const { t: tr } = useTranslation('mail-templates');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className={`max-h-[90vh] w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} overflow-y-auto rounded-lg bg-white dark:bg-gray-900 p-5 shadow-xl`}
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <h2 id={titleId} className="truncate text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label={tr('close')}
            className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60"
          >
            ✕
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

/**
 * Convert OMS's JSON-schema-shaped variables back to the simple
 * key/type rows the form editor uses. Only handles flat
 * type: 'object' + properties shape — anything more complex
 * preserves but the editor can't roundtrip it (rare case for
 * customers building templates manually outside this UI).
 */
function schemaToList(
  schema: Record<string, unknown> | null,
): VariableRow[] {
  if (!schema || typeof schema !== 'object') return [];
  const props = schema.properties;
  if (!props || typeof props !== 'object') return [];
  return Object.entries(props as Record<string, unknown>).map(([name, def]) => {
    const t =
      def && typeof def === 'object' && typeof (def as { type?: string }).type === 'string'
        ? ((def as { type: string }).type as VariableRow['type'])
        : 'string';
    return {
      name,
      type: t === 'number' || t === 'boolean' ? t : 'string',
    };
  });
}

function formatDate(iso: string): string {
  return fmtDate(iso, iso);
}
